import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Space, Select, DatePicker, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getVoidRecords } from '../../api/serial';
import { useVisibleFields } from '../../hooks/usePermission';

const { RangePicker } = DatePicker;

const VOID_TYPE_OPTIONS = ['导入格式错误', '重复导入', '手动作废'].map((v) => ({ value: v, label: v }));
const VOID_TYPE_COLOR = { '导入格式错误': 'orange', '重复导入': 'red', '手动作废': 'default' };

const VoidRecords = () => {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchList = async (params = {}) => {
        setLoading(true);
        try {
            const res = await getVoidRecords({ page: params.page || 1, page_size: params.page_size || 10, ...params });
            if (res.code === 0) {
                setDataSource(res.data.list);
                setPagination((prev) => ({ ...prev, total: res.data.total }));
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch void records failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const voidType = searchParams.get('void_type');
        const voidTime = searchParams.get('void_time');
        if (voidType || voidTime) {
            const values = {};
            if (voidType) values.void_type = voidType;
            if (voidTime) values.void_time = [dayjs(voidTime), dayjs(voidTime)];
            form.setFieldsValue(values);
        }
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        fetchList({ ...form.getFieldsValue(), page: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        fetchList({ page: 1 });
    };

    const visibleFields = useVisibleFields('serial:void-records');
    const show = (code) => visibleFields.has(code);

    const columns = [
        show('mac') && { title: 'MAC', dataIndex: 'mac', key: 'mac', width: 180, render: (v) => v || '-' },
        show('void_type') && {
            title: '作废类型', dataIndex: 'void_type', key: 'void_type', width: 130,
            render: (v) => <Tag color={VOID_TYPE_COLOR[v] || 'default'}>{v}</Tag>,
        },
        show('platform') && { title: '平台', dataIndex: 'platform', key: 'platform', width: 90 },
        show('fields') && {
            title: '平台字段', dataIndex: 'fields', key: 'fields',
            render: (fields) => (
                <Space wrap size={4}>
                    {Object.entries(fields || {}).map(([k, v]) => (
                        <Tag key={k}>{k}：{v}</Tag>
                    ))}
                </Space>
            ),
        },
        show('void_time') && { title: '作废时间', dataIndex: 'void_time', key: 'void_time', width: 180 },
    ].filter(Boolean);

    return (
        <div style={{ padding: '16px' }}>
            <Form form={form} layout="inline" style={{ marginBottom: 16, rowGap: 16 }}>
                <Form.Item name="void_type">
                    <Select placeholder="作废类型" allowClear options={VOID_TYPE_OPTIONS} style={{ width: 150 }} />
                </Form.Item>
                <Form.Item name="void_time">
                    <RangePicker placeholder={['开始日期', '结束日期']} style={{ width: 240 }} />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                    </Space>
                </Form.Item>
            </Form>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                scroll={{ x: 1200 }}
            />
        </div>
    );
};

export default VoidRecords;
